import { FC, useEffect, useRef, useState } from 'react';

import { getMyIsuzuStore } from '@/app/utils/my-isuzu';
import { scrollTo } from '@/components/hooks/hash';
import useCheckInitialRender from '@/components/hooks/useCheckInitialRender';
import { useDealerSearch } from '@/components/hooks/useDealerSearch';
import { Button } from '@/components/shared/buttons/button';
import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Select } from '@/components/shared/form/select';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { formatTel } from '@/components/shared/utility/format';
import { googleMapURL } from '@/components/shared/utility/location';
import { IDealer } from '@/types/dealer/dealer';
import { IDistrict } from '@/types/dealer/district';
import { IProvince } from '@/types/dealer/province';
import { IVehicleType } from '@/types/services/contentstack';
import { IIcon } from '@/types/shared';

interface SearchDealerContactProps {
  isActive: boolean;
  title: string;
  vehicleType: IVehicleType;
  provinces: any[];
  dataTrack?: string;
}

export const ContactLink = ({
  icon,
  label,
  href,
}: {
  icon?: IIcon;
  label: string;
  href: string;
}) => {
  return (
    <Flex columnGap="4">
      {!!icon && (
        <Icon
          icon={icon}
          size="sm"
          color={icon === 'my-isuzu' ? 'icon-white' : 'icon-red'}
        />
      )}
      <Flex css={{ flex: 1 }}>
        <Link
          href={`${href}`}
          test={{
            dataTest: 'contact-dealer',
          }}
          target="_blank"
          tracking={{
            dataTrack: 'contact-dealer',
            dataTrackText: `service_line_${label}`,
            dataTrackUrl: `${href}`,
          }}
        >
          <Text
            size="text-base"
            css={{
              textDecoration: 'underline',
            }}
          >
            {label}
          </Text>
        </Link>
      </Flex>
    </Flex>
  );
};

export const WorkingHours = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  const isOpen =
    /^([0-1]?[0-9]|2[0-3]):[0-5][0-9] - ([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(
      description
    );
  return (
    <Flex columnGap="4">
      <Flex justifyContent="start" css={{ flex: 1 }}>
        <Box
          backgroundColor={isOpen ? 'red' : 'gray200'}
          rounded="1"
          paddingX="2"
          paddingY="1"
        >
          <Text
            size="text-base-2"
            font="bold"
            variant={isOpen ? 'text-white' : 'text-gray'}
          >
            {title}
          </Text>
        </Box>
      </Flex>
      <Flex css={{ flex: 1, justifyContent: 'end' }}>
        <Box
          backgroundColor="gray200"
          rounded="1"
          paddingX="2"
          paddingY="1"
          css={{
            width: '$size-28',
            textAlign: 'center',
          }}
        >
          <Text
            size="text-base-2"
            font="bold"
            variant={isOpen ? 'text-gray-dark' : 'text-gray'}
          >
            {description}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

export const SearchDealerContact: FC<SearchDealerContactProps> = ({
  provinces,
  title,
  vehicleType,
  dataTrack,
}) => {
  const dealerSearchData = useDealerSearch({
    provinceOptions: provinces,
    vehicleType: vehicleType,
    isSetPath: false,
  });
  const [dealer, setDealer] = useState<IDealer | null>(null);
  const CONTENT_REF = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const isInitialRender = useCheckInitialRender();

  const getSortedProvince = (provinces: IProvince[]) => {
    return provinces.sort((a, b) => a.name_th.localeCompare(b.name_th, 'th'));
  };

  const getSortedDistrict = (districts: IDistrict[]) => {
    return districts.sort((a, b) => a.name_th.localeCompare(b.name_th, 'th'));
  };

  const onProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dealerSearchData.onProvinceChange &&
      dealerSearchData.onProvinceChange(e.target.value);
  };

  const onDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dealerSearchData.onDistrictChange &&
      dealerSearchData.onDistrictChange(e.target.value);
  };

  const onDealerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const _dealer = dealerSearchData.dealers.filter(
      (o) => o.uid === e.target.value
    );
    _dealer && _dealer.length > 0 && setDealer(_dealer[0]);
  };

  const onClick = () => {
    if (!CONTENT_REF.current) return;
    scrollTo(CONTENT_REF.current, -16);
  };

  useEffect(() => {
    if (isInitialRender) return;
    dealerSearchData.submitSearch &&
      dealerSearchData.submitSearch({
        provinceID: `${dealerSearchData.selectedProvinceID}`,
        districtIDs: `${dealerSearchData.selectedDistrictID}`,
        vehicleType: vehicleType,
        service: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dealerSearchData.selectedProvinceID,
    dealerSearchData.selectedDistrictID,
  ]);

  useEffect(() => {
    if (!CONTENT_REF) return;
    const handleScroll = () => {
      if (CONTENT_REF.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const rect = CONTENT_REF.current.getBoundingClientRect();
        const isVisible =
          rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) +
            rect.height;
        setIsVisible(isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Box
        display="flex"
        width="full"
        height="auto"
        justifyContent="center"
        alignItems="center"
        paddingX="6"
        paddingY="4"
        position="fixed"
        zIndex="10"
        boxShadow="400"
        bottom="0"
        left="0"
        right="0"
        backgroundColor="white"
        css={{
          opacity: isVisible ? 0 : 1,
          transitionProperty: 'transform, opacity',
          transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
          transitionDuration: '0.6s',
          visibility: isVisible ? 'hidden' : 'visible',
          '@lg': {
            display: 'none',
          },
        }}
        test={{
          dataTest: 'cta_bottom_button',
        }}
        tracking={{
          dataTrack: 'offer-detail',
          dataTrackText: title,
          dataTrackSection: dataTrack,
        }}
      >
        <Button
          label={title}
          variant="button-red"
          isMinWidth
          onClick={onClick}
        />
      </Box>
      <Box
        backgroundColor="gray200"
        rounded="2"
        padding={{ '@maxlg': '4', '@lg': '6' }}
        test={{
          dataTest: 'dealer_search_contact',
        }}
      >
        <Flex
          direction="column"
          rowGap="3"
          ref={CONTENT_REF}
          css={{
            scrollMarginTop: '$space-10',
          }}
        >
          <Text
            as="div"
            size="text-base"
            font="bold"
            css={{
              flexGrow: 1,
              '@maxlg': {
                textAlign: 'center',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <Flex
            direction={{ '@maxxl': 'column', '@xl': 'row' }}
            columnGap="4"
            rowGap="3"
          >
            <Box
              css={{
                flex: '0 0 100%',
                maxWidth: '100%',
                '@lg': {
                  flex: '1 1 0%',
                },
              }}
            >
              <Select
                label="จังหวัด"
                required
                name="province"
                value={dealerSearchData.selectedProvinceID}
                disabled={isInitialRender}
                onChange={onProvinceChange}
              >
                <option value="">เลือกจังหวัด</option>
                {getSortedProvince(provinces).map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
            </Box>
            <Box
              css={{
                flex: '0 0 100%',
                maxWidth: '100%',
                '@lg': {
                  flex: '1 1 0%',
                },
              }}
            >
              <Select
                label="เขต/อำเภอ"
                name="district"
                value={dealerSearchData.selectedDistrictID}
                disabled={dealerSearchData.districts?.length === 0}
                onChange={onDistrictChange}
              >
                <option value="">เลือกเขต/อำเภอ</option>
                {getSortedDistrict(dealerSearchData.districts).map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>
          <Box
            css={{
              flex: '0 0 100%',
              maxWidth: '100%',
              '@lg': {
                flex: '1 1 0%',
              },
            }}
          >
            <Select
              label="ศูนย์บริการ"
              required
              name="dealer"
              disabled={
                dealerSearchData.districts?.length === 0 ||
                dealerSearchData.dealers?.length === 0
              }
              onChange={onDealerChange}
            >
              <option value="">เลือกศูนย์บริการ</option>
              {dealerSearchData.districts?.length > 0 &&
                dealerSearchData.dealers.map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.mirai?.name_th}
                  </option>
                ))}
            </Select>
          </Box>

          {!!dealer?.uid && (
            <Box
              backgroundColor="white"
              rounded="2"
              padding={{ '@maxlg': '4', '@lg': '6' }}
              test={{
                dataTest: 'dealer_search_contact_result',
              }}
            >
              <Flex direction="column" rowGap="6">
                <Flex justifyContent="between" columnGap="2">
                  <Flex direction="column" rowGap="2">
                    <Text as="div" size="text-base" font="bold">
                      {dealer?.mirai?.name_th}
                    </Text>
                    <Text size="text-base">{dealer?.mirai?.address}</Text>
                  </Flex>
                  <Link
                    href={googleMapURL({
                      lat: dealer.sale?.lat ?? '',
                      lng: dealer.sale?.lon ?? '',
                      name: dealer.mirai?.name_th,
                      url: dealer.sale?.google_map_url,
                    })}
                    css={{ alignSelf: 'end' }}
                    test={{
                      dataTest: 'button_gotoGooglemap',
                    }}
                    target="_blank"
                    tracking={{
                      dataTrack: 'contact-dealer',
                      dataTrackText: `lcv_sale_google_map`,
                      dataTrackUrl: googleMapURL({
                        lat: dealer.sale?.lat ?? '',
                        lng: dealer.sale?.lon ?? '',
                        name: dealer.mirai?.name_th,
                        url: dealer.sale?.google_map_url,
                      }),
                    }}
                  >
                    <Box width="12" height="12">
                      <Image
                        src="https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blte15ae43a10394f48/66b087671370b63baf9d0698/search-dealer-contact-map-icom.jpg"
                        alt={dealer?.mirai?.name_th ?? ''}
                        width={48}
                        height={48}
                        layout="responsive"
                        aspectRatio="1by1"
                      />
                    </Box>
                  </Link>
                </Flex>

                <Grid
                  columnGap="6"
                  rowGap="2"
                  alignItems="center"
                  css={{
                    '@maxxl': {
                      gridAutoFlow: 'row',
                      gridTemplateColumns: 'repeat(1, 1fr)',
                    },
                    '@xl': {
                      gridAutoFlow: 'column',
                      gridTemplateRows: 'repeat(4, 1fr)',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <Text as="div" size="text-base-2" font="bold">
                    วันและเวลาทำการ
                  </Text>
                  <WorkingHours
                    title="จ - ศ"
                    description={
                      dealer?.aftersale?.service_center_hour_mon_fri ?? ''
                    }
                  />
                  <WorkingHours
                    title="ส"
                    description={
                      dealer?.aftersale?.service_center_hour_sat ?? ''
                    }
                  />
                  <WorkingHours
                    title="อา"
                    description={
                      dealer?.aftersale?.service_center_hour_sun ?? ''
                    }
                  />
                  <Text
                    as="div"
                    size="text-base-2"
                    font="bold"
                    css={{ '@maxxl': { mt: '$space-2' } }}
                  >
                    ข้อมูลการติดต่อ
                  </Text>
                  <ContactLink
                    icon="local-phone"
                    label={
                      dealer?.aftersale?.service_center_appointment_tel?.[0]
                        ?.tel
                        ? `${formatTel(
                            dealer?.aftersale
                              ?.service_center_appointment_tel?.[0]?.tel,
                            true
                          )} ${
                            dealer?.aftersale
                              ?.service_center_appointment_tel?.[0]?.ext
                              ? ` ต่อ ${dealer?.aftersale?.service_center_appointment_tel?.[0]?.ext}`
                              : ''
                          }`
                        : ''
                    }
                    href={
                      dealer?.aftersale?.service_center_appointment_tel?.[0]
                        ?.tel
                        ? `tel:${formatTel(
                            dealer?.aftersale?.service_center_appointment_tel[0]
                              ?.tel,
                            true
                          )}`
                        : ''
                    }
                  />
                  <ContactLink
                    icon="line"
                    label={dealer?.aftersale?.line_service ?? ''}
                    href={`https://line.me/R/ti/p/${dealer?.aftersale?.line_service}`}
                  />
                  <ContactLink
                    icon="my-isuzu"
                    label="my-ISUZU application"
                    href={getMyIsuzuStore()}
                  />
                </Grid>
              </Flex>
            </Box>
          )}
        </Flex>
      </Box>
    </>
  );
};
SearchDealerContact.displayName = 'SearchSection';
